import { useState, useCallback, startTransition } from 'react';
import { ClientesService } from '../services/clientes.service';
import { toast } from 'sonner';

interface UseCPFValidationProps {
  clienteId?: string; // ID do cliente sendo editado (para ignorar na validação)
}

export const useCPFValidation = ({ clienteId }: UseCPFValidationProps = {}) => {
  const [isValidating, setIsValidating] = useState(false);
  const [cpfExists, setCpfExists] = useState(false);

  const validateCPF = useCallback((cpf: string) => {
    if (!cpf || cpf.length < 11) {
      startTransition(() => {
        setCpfExists(false);
        setIsValidating(false);
      });
      return;
    }

    // Remove formatação do CPF/CNPJ
    const cleanCPF = cpf.replace(/\D/g, '');
    
    // Aceita CPF (11 dígitos) ou CNPJ (14 dígitos)
    if (cleanCPF.length !== 11 && cleanCPF.length !== 14) {
      startTransition(() => {
        setCpfExists(false);
        setIsValidating(false);
      });
      return;
    }

    // Usar startTransition para evitar suspensão durante atualização
    startTransition(() => {
      setIsValidating(true);
    });
    
    // Executar validação de forma assíncrona sem bloquear a UI
    Promise.resolve().then(async () => {
      try {
        const exists = await ClientesService.verificarCPFExistente(cleanCPF, clienteId);
        
        // Usar startTransition para atualizar o estado
        startTransition(() => {
          setCpfExists(exists);
          setIsValidating(false);
        });
        
        if (exists) {
          // Usar timeout para evitar suspensão durante toast
          const docType = cleanCPF.length === 11 ? 'CPF' : 'CNPJ';
          setTimeout(() => {
            toast.warning(`Este ${docType} já está cadastrado no sistema`);
          }, 0);
        }
      } catch (error) {
        console.error('Erro ao validar CPF/CNPJ:', error);
        startTransition(() => {
          setCpfExists(false);
          setIsValidating(false);
        });
      }
    });
  }, [clienteId]);

  const resetValidation = useCallback(() => {
    startTransition(() => {
      setCpfExists(false);
      setIsValidating(false);
    });
  }, []);

  return {
    validateCPF,
    resetValidation,
    isValidating,
    cpfExists
  };
}; 